import { createQueryKeys } from '@lukemorales/query-key-factory';

import type { SearchOrdersParams } from './useSearchOrders';

/**
 * Query keys for orders API calls using contextQueries pattern
 *
 * Structure mirrors OrderDetailsDrawer tabs:
 * - detail (parent context)
 *   - progress (tab + action items)
 *   - paymentFai (tab)
 *   - documents (tab)
 *   - timeline (tab)
 * - list (isolated - Algolia store)
 * - stats (isolated - aggregate data)
 *
 * Example usage:
 * ```ts
 * // Query a specific tab
 * const servicesProviderId = useCurrentServicesProviderId();
 * useQuery(queries.orders.detail(servicesProviderId, orderId)._ctx.progress);
 *
 * // Invalidate entire order (all tabs)
 * queryClient.invalidateQueries({
 *   queryKey: queries.orders.detail(servicesProviderId, orderId).queryKey
 * });
 *
 * // Invalidate specific tab
 * queryClient.invalidateQueries({
 *   queryKey: queries.orders.detail(servicesProviderId, orderId)._ctx.progress.queryKey
 * });
 *
 * // Invalidate ALL orders queries
 * queryClient.invalidateQueries({
 *   queryKey: queries.orders._def
 * });
 * ```
 */
export const orders = createQueryKeys('orders', {
	// Isolated: Algolia-based search (different data store)
	list: (servicesProviderId: string, params?: SearchOrdersParams) => ({
		queryKey: [servicesProviderId, params],
	}),

	// Isolated: Aggregate stats (not order-specific)
	stats: (servicesProviderId: string) => ({
		queryKey: [servicesProviderId],
	}),

	// Parent context: Order detail with nested tab contexts
	detail: (servicesProviderId: string, orderId: string) => ({
		queryKey: [servicesProviderId, orderId],

		contextQueries: {
			// PROGRESS TAB - Action items and progress tracking
			progress: {
				queryKey: null,

				contextQueries: {
					// GET endpoints for specific action items
					assignVin: {
						queryKey: null,
					},
					assignOdometerReading: {
						queryKey: null,
					},
					assignMotorcycleEngineNumber: {
						queryKey: null,
					},
					markAssembled: {
						queryKey: null,
					},
					markInspected: {
						queryKey: null,
					},
					markInTransitToFulfillmentCenter: {
						queryKey: null,
					},
					markInTransitToBuyer: {
						queryKey: null,
					},
					markDeliveryFulfilled: {
						queryKey: null,
					},
					markPickupScheduled: {
						queryKey: null,
					},
					markPickupFulfilled: {
						queryKey: null,
					},
					markBackfillInventoryAddedToInventorySystem: {
						queryKey: null,
					},
					markBackfillAccessoriesRequested: {
						queryKey: null,
					},
					markReceivedAtFulfillmentCenter: {
						queryKey: null,
					},
					markProductionComplete: {
						queryKey: null,
					},
					generateTempTag: {
						queryKey: null,
					},
					completeWarrantyRegistration: {
						queryKey: null,
					},
					finalizeDealInInventorySystem: {
						queryKey: null,
					},
					generatePlate: {
						queryKey: null,
					},
					completeMSO: {
						queryKey: null,
					},
					scanAndUploadForms: {
						queryKey: null,
					},
					submitSignedTarPaperwork: {
						queryKey: null,
					},
					shipBuyerPacketToSeller: {
						queryKey: null,
					},
					shipSellerFormsToEkho: {
						queryKey: null,
					},
					approveRobosignTarPaperwork: {
						queryKey: null,
					},
					approveCounterSignRobosignTarPaperwork: {
						queryKey: null,
					},
					sellerRonSession: {
						queryKey: null,
					},
				},
			},

			// PAYMENT & F&I TAB
			paymentFai: {
				queryKey: null,
			},

			// DOCUMENTS TAB
			documents: {
				queryKey: null,
			},

			// TIMELINE TAB
			timeline: {
				queryKey: null,
			},
		},
	}),
});
